import React, { useState } from "react";
import PosCustomIcon from "../../../../../customComponents/PosCustomIcon/PosCustomIcon";
import "./KdsCompletedCard.css";
import swiggyLogo from "../../../../../assets/images/swiggy.png";
import ZomatoLogo from "../../../../../assets/images/zomato.png";
import websiteLogo from "../../../../../assets/images/cutlery.png";
import uberEatsLogo from "../../../../../assets/images/uberEats.jpg";
import whatsappLogo from "../../../../../assets/images/whatsapp.png";
import { PosCustomButton } from "../../../../../customComponents/PosCustomButton/PosCustomButton";
import reorderIcon from "../../../../../assets/images/kds-arrow-path.png";
import { Tooltip } from "@mui/material";

// Sub order type display mapping
const SUB_ORDER_TYPE_MAP = {
  DINE_IN: "Dine In",
  TAKE_AWAY: "Take Away",
  DELIVERY: "Delivery",
};

const KdsCompletedCard = ({
  mode = "completed", // "completed" | "redo"
  prepTime = "N/A",
  tokenNumber,
  orderType,
  subOrderType,
  cartId,
  user,
  items,
  jobId,
  updatedTime,
  onReverseOrder,
  onReprintBill,
}) => {
  const [reverseOrderLoader, setReverseOrderLoader] = useState(false);
  const [printBillLoader, setPrintBillLoader] = useState(false);

  // Map order types to logo, name and color
  const orderTypeMap = {
    ZOMATO_ORDER: { logo: ZomatoLogo, name: "Zomato", color: "#E23744" },
    SWIGGY_ORDER: { logo: swiggyLogo, name: "Swiggy", color: "#FC8019" },
    WHATSAPP_ORDER: { logo: whatsappLogo, name: "WhatsApp", color: "#25D366" },
    UBER_EATS_ORDER: { logo: uberEatsLogo, name: "Uber Eats", color: "#000000" },
    B2C_ORDER: { logo: websiteLogo, name: "Website Order", color: "#0860E6" },
  };

  // Use orderType if it's an external order, otherwise fall back to POS
  const isExternalOrder = [
    "ZOMATO_ORDER",
    "SWIGGY_ORDER",
    "WHATSAPP_ORDER",
    "UBER_EATS_ORDER",
    "B2C_ORDER",
  ].includes(orderType);
  const { logo, name } = isExternalOrder
    ? orderTypeMap[orderType]
    : { logo: websiteLogo, name: "POS" };
  const textColor = isExternalOrder ? orderTypeMap[orderType]?.color : "#374151";

  const handleReverseOrderClick = async () => {
    if (onReverseOrder) {
      setReverseOrderLoader(true);
      try {
        await onReverseOrder(jobId);
      } catch (error) {
        // Handle error if needed, but it's typically handled in the parent
      } finally {
        setReverseOrderLoader(false);
      }
    }
  };

  const handleReprintBillClick = async (cartId) => {
    if (onReprintBill) {
      setPrintBillLoader(true);
      try {
        await onReprintBill(cartId);
      } catch (error) {
        // Handle error if needed
      } finally {
        setPrintBillLoader(false);
      }
    }
  };

  const formatTime = (utcTime) => {
    if (!utcTime) return "";

    const dateWithZ = utcTime.endsWith("Z") ? utcTime : utcTime + "Z";
    return new Date(dateWithZ).toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formattedTime = formatTime(updatedTime);

  return (
    <div className="kdsCompletedCardContainer">
      <div className="kdsCompletedCardHeader">
        <p className="kdsCompletedCartLeft">{jobId}</p>
        <div className="kdsCompletedCartRight">
          <p className="kdsCompCardUpdateTime">{formattedTime}</p>
          <p className="kdsCompCardPrepTime">Prep: {prepTime} Mins</p>
        </div>
      </div>

      <div className="KdsCompModeContainer">
        <div className="kdsCompModeLeft">
          <PosCustomIcon image={logo} height={"16px"} width={"16px"} />
          <span className="kdsCompBrandName">{name}</span>
        </div>
        <div className="kdsCompModeRight">
          <p className="kdsCompletedCartLeft">#{tokenNumber && tokenNumber}</p>
          <span className="kdsCompOderIdDiv">|</span>
          <span className="kdsCompSubOrderType">
            {subOrderType ? SUB_ORDER_TYPE_MAP[subOrderType] || subOrderType : "NA"}
          </span>
        </div>
      </div>

      {user && (
        <Tooltip title={user}>
          <p className="kdsCompCardCustomer">{user}</p>
        </Tooltip>
      )}

      <div className="kdsCompItemsList">
        {items &&
          items?.map((item) => (
            <div className="kdsCompItem" key={item?.taskId}>
              <div className="kdsCompItemMain">
                <p
                  className={`kdsCompItemTitle ${
                    item?.quantity == item?.returnedQuantity ? "kdsCompCartReturnedQty" : ""
                  }`}
                >
                  {item?.title}
                </p>
                <span
                  className={`kdsCompItemQty ${
                    item?.quantity == item?.returnedQuantity ? "kdsCompCartReturnedQty" : ""
                  }`}
                >
                  x{item?.quantity}
                </span>
              </div>

              <div className="kdsCompChipsContainer">
                {item?.metadata?.comments && (
                  <div className="kdsCompCardChipsYellow">
                    <p className="kdsCompCardTextYello">{item?.metadata?.comments}</p>
                  </div>
                )}

                {item?.additionalProperties?.addons &&
                  item?.additionalProperties?.addons?.map((addOn, idx) => (
                    <div className="kdsCompCardChipsGrey" key={idx}>
                      <p className="kdsCompCardText">{addOn?.itemName}</p>
                    </div>
                  ))}
              </div>
            </div>
          ))}
      </div>

      <div className="kdsCompItemActionButton" style={mode === "redo" ? { justifyContent: "center" } : {}}>
        {mode === "completed" && cartId && (
          <PosCustomButton
            title="Re-print Bill"
            transparent={true}
            radius="50px"
            p={"12px 12px"}
            customBackgroundColor={"#E8F3FF"}
            customColor={"#2A71FF"}
            variant={"custom-variant"}
            customHover={{ background: "#dcebff", color: "#2A71FF" }}
            fontWeight={500}
            fontSize={"13px"}
            iconOnRight={
              <PosCustomIcon
                icon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#2A71FF"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-4 w-4"
                    aria-hidden="true"
                  >
                    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                    <path d="M6 9V3a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v6"></path>
                    <rect x="6" y="14" width="12" height="8" rx="1"></rect>
                  </svg>
                }
                height={"16px"}
                width={"16px"}
              />
            }
            width={"100%"}
            iconPosition="corner"
            onClickFunction={() => handleReprintBillClick(cartId)}
            loading={printBillLoader}
          />
        )}
        {mode === "completed" && (
          <PosCustomButton
            title="Reverse Order"
            transparent={true}
            radius="50px"
            p={"12px 12px"}
            customBackgroundColor={"#F04438 "}
            customColor={"var(--surface-white)"}
            variant={"custom-variant"}
            customHover={{ background: "#dd3c30", color: "#FFFFFF" }}
            fontWeight={500}
            fontSize={"13px"}
            iconOnRight={
              <PosCustomIcon
                icon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#FFFFFF"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-4 w-4"
                    aria-hidden="true"
                  >
                    <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
                    <path d="M3 3v5h5"></path>
                    <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"></path>
                    <path d="M16 16h5v5"></path>
                  </svg>
                }
                height={"16px"}
                width={"16px"}
                color="currentColor"
              />
            }
            iconPosition="corner"
            onClickFunction={handleReverseOrderClick}
            loading={reverseOrderLoader}
            width={"100%"}
          />
        )}
        {mode === "redo" && (
          <PosCustomButton
            title="Re-Queue Order"
            radius="50px"
            p={"12px 12px"}
            customBackgroundColor={"#F04438 "}
            customColor={"var(--surface-white)"}
            variant={"custom-variant"}
            customHover={{ background: "#dd3c30", color: "#FFFFFF" }}
            fontWeight={500}
            fontSize={"13px"}
            transparent={true}
            iconOnRight={<PosCustomIcon image={reorderIcon} height={"13px"} width={"13px"} />}
            iconPosition="center"
            onClickFunction={handleReverseOrderClick}
            loading={reverseOrderLoader}
            width={"100%"}
          />
        )}
      </div>
    </div>
  );
};

export default KdsCompletedCard;
